import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AdminPagesAudit from '../components/AdminPagesAudit';
import {
  AdminMessaging,
  AdminMailing,
  AdminBrevo,
  AdminScheduleConfig,
  AdminLeaveManagement,
  AdminLocalisation,
  AdminBackup,
  AdminSecurity,
  AdminLoginLogs,
  AdminArchitecture,
  AdminExport
} from '../components/admin';
import { 
  Settings, 
  Mail, 
  Send,
  Globe,
  Tag,
  HardDrive,
  Shield,
  Clock,
  Palmtree,
  Lock,
  FileText,
  Layers,
  FileDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  
  const [config, setConfig] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_pass: '',
    email_from: '',
    work_start_hour: '6',
    work_end_hour: '22',
    holiday_country: 'CH',
    default_portal_language: 'de',
    default_day_duration: 8.5,
    default_break_duration: 30,
    break_threshold_hours: 6,
    leave_notification_email: '',
    email_signature: '',
    email_signature_logo_url: ''
  });

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/config`);
      setConfig(prev => ({
        ...prev,
        ...res.data,
        smtp_pass: res.data.smtp_pass === '***' ? '' : (res.data.smtp_pass || ''),
        _smtp_pass_set: res.data.smtp_pass === '***'
      }));
      if (user?.email) {
        setTestEmailAddress(user.email);
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSaveConfig = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, {
        section,
        ...config
      });
      toast.success(t('configSaved'));
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl">{t('administration').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">{t('systemConfiguration')}</p>
        </div>
      </div>

      <Tabs defaultValue="mailing" className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:grid-cols-12 max-w-6xl">
          <TabsTrigger value="mailing" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Mailing
          </TabsTrigger>
          <TabsTrigger value="messaging" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Messagerie
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Brevo
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            {t('workHours')}
          </TabsTrigger>
          <TabsTrigger value="leave" className="flex items-center gap-2">
            <Palmtree className="h-4 w-4" />
            {t('leave')}
          </TabsTrigger>
          <TabsTrigger value="localisation" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Localisation
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Sécurité
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="pages" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Pages
          </TabsTrigger>
          <TabsTrigger value="architecture" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Archi
          </TabsTrigger>
          <TabsTrigger value="backup" className="flex items-center gap-2">
            <HardDrive className="h-4 w-4" />
            Backup
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2" data-testid="admin-tab-export">
            <FileDown className="h-4 w-4" />
            Export
          </TabsTrigger>
        </TabsList>

        {/* Messaging Configuration */}
        <TabsContent value="messaging" className="mt-6">
          <AdminMessaging 
            config={config} 
            setConfig={setConfig} 
            onSave={handleSaveConfig} 
            saving={saving} 
          />
        </TabsContent>

        {/* Mailing Configuration */}
        <TabsContent value="mailing" className="mt-6">
          <AdminMailing />
        </TabsContent>

        {/* Email/Brevo Configuration */}
        <TabsContent value="email" className="mt-6">
          <AdminBrevo 
            config={config} 
            setConfig={setConfig} 
            onSave={handleSaveConfig} 
            saving={saving}
            testEmailAddress={testEmailAddress}
            setTestEmailAddress={setTestEmailAddress}
          />
        </TabsContent>

        {/* Work Hours Configuration */}
        <TabsContent value="schedule" className="mt-6">
          <AdminScheduleConfig 
            config={config} 
            setConfig={setConfig} 
            onSave={handleSaveConfig} 
            saving={saving}
            language={language}
          />
        </TabsContent>

        {/* Leave Management */}
        <TabsContent value="leave" className="mt-6">
          <AdminLeaveManagement />
        </TabsContent>

        {/* Localisation Configuration */}
        <TabsContent value="localisation" className="mt-6">
          <AdminLocalisation 
            config={config} 
            setConfig={setConfig} 
            onSave={handleSaveConfig} 
            saving={saving}
          />
        </TabsContent>

        {/* Pages Management */}
        <TabsContent value="pages" className="mt-6">
          <AdminPagesAudit />
        </TabsContent>

        {/* Architecture */}
        <TabsContent value="architecture" className="mt-6">
          <AdminArchitecture />
        </TabsContent>

        {/* Security Configuration */}
        <TabsContent value="security" className="mt-6">
          <AdminSecurity />
        </TabsContent>

        {/* Login Logs */}
        <TabsContent value="logs" className="mt-6">
          <AdminLoginLogs />
        </TabsContent>

        {/* Backup Tab */}
        <TabsContent value="backup" className="mt-6">
          <AdminBackup />
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="mt-6" data-testid="admin-tab-content-export">
          <AdminExport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
